import { useEffect, useMemo, useState } from 'react'

function preloadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = function() {
      resolve(img)
    }
    img.onerror = img.onabort = function() {
      reject(src)
    }
    img.src = src
  })
}

export default function useImagePreloader(data) {
    const [imagesPreloaded, setImagesPreloaded] = useState(false)

    const themeImagesFront = data && data.themes && Object.values(data.themes).map(t => t.imageFront.url)
	const tableImagesFront = data && data.tables && Object.values(data.tables).map(t => t.imageFront.url)
	const ringImagesFront = data && data.rings && Object.values(data.rings).map(t => t.imageFront.url)
    const themeImagesThumbnail = data && data.themes && Object.values(data.themes).filter(t => !!t.imageThumbnail).map(t => t.imageThumbnail.url)
	const tableImagesThumbnail = data && data.tables && Object.values(data.tables).filter(t => !!t.imageThumbnail).map(t => t.imageThumbnail.url)
	const ringImagesThumbnail = data && data.rings && Object.values(data.rings).filter(t => !!t.imageThumbnail).map(t => t.imageThumbnail.url)
	
    const frontImages = useMemo(() => [
        ...themeImagesFront,
        ...tableImagesFront,
        ...ringImagesFront
    ], [themeImagesFront, tableImagesFront, ringImagesFront])

    const thumbnailImages = useMemo(() => [
        ...themeImagesThumbnail,
        ...tableImagesThumbnail,
        ...ringImagesThumbnail
    ], [themeImagesThumbnail, tableImagesThumbnail, ringImagesThumbnail])

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    useEffect(() => {
        let isCancelled = false

        window.loadedThumbnails = []
        window.loadedFrontImages = []

        async function effect() {
            const frontImagesPromiseList = []
            const thumbnailImagesPromiseList = []

            if (isCancelled) {
                return
            }
        
            if (window.loadedThumbnails.length !== thumbnailImagesPromiseList.length) {
                // At the start of the app, wait 1 seconds before starting preloading process
                await sleep(1000);
                // First, load thumbnails (they are small, this should be quick)
                for (const i of thumbnailImages) {
                    if (!window.loadedThumbnails.includes(i)) {
                        thumbnailImagesPromiseList.push(preloadImage(i))
                    }
                }

                const thumbs = await Promise.all(thumbnailImagesPromiseList)
                window.loadedThumbnails.push(thumbs.map(t => t.currentSrc))
            }

            if (window.loadedFrontImages.length !== frontImagesPromiseList.length) {
                // Second, wait and then preload front images
                await sleep(3000);
            
                for (const i of frontImages) {
                    if (!window.loadedFrontImages.includes(i)) {
                        frontImagesPromiseList.push(preloadImage(i))
                    }
                }
                const fronts1 = await Promise.all(frontImagesPromiseList.slice(0, 20))
                window.loadedFrontImages.push(fronts1.map(t => t.currentSrc))

                // Load next set of 20+20
                const fronts2 = await Promise.all(frontImagesPromiseList.slice(21, 40))
                window.loadedFrontImages.push(fronts2.map(t => t.currentSrc))

                // Load the rest
                await sleep(5000);
                const frontsRest = await Promise.all(frontImagesPromiseList.slice(41))
                window.loadedFrontImages.push(frontsRest.map(t => t.currentSrc))
            }

            if (isCancelled) {
                return
            }

            setImagesPreloaded(true)
        }

        effect()

        return () => {
            isCancelled = true
        }
    }, [frontImages, thumbnailImages])

    return { imagesPreloaded }
}